import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  Dropdown,
  Tooltip,
  Input,
} from 'antd';
import type { Breakpoint } from 'antd/es/_util/responsiveObserver';
import type { ColumnsType } from 'antd/es/table';
import { format, isPast, isToday, parseISO } from 'date-fns';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CreditCardResponse2 } from '../../model/Credit-card.model';
import CreditCardService from '../../service/credit-card-service/Credit-card.service';
import EditCreditCardModal from './EditCreditCardModal';
import { useCardDetail } from '../../context/CardDetailContext';

interface CreditCardListProps {
  creditCards: CreditCardResponse2[];
  onRefresh: () => void;
  customerId: number;
}

const CreditCardList: React.FC<CreditCardListProps> = ({
  creditCards,
  onRefresh,
  customerId,
}) => {
  const [cardToDelete, setCardToDelete] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [cyclePaymentDateFilter, setCyclePaymentDateFilter] = useState<
    number[]
  >([]);
  const navigate = useNavigate();
  const pageSize = 5;
  const { confirm } = Modal;
  const { setSelectedCard } = useCardDetail(); // Thêm dòng này

  // Lọc dữ liệu theo cyclePaymentDateFilter
  const filteredCreditCards = creditCards.filter((card) => {
    if (cyclePaymentDateFilter.length > 0) {
      return cyclePaymentDateFilter.includes(card.cyclePaymentDate || 0);
    }
    return true;
  });

  // Tạo các filters cho các cột
  const getUniqueValues = (dataIndex: string) => {
    const uniqueValues = new Set<string>();

    creditCards.forEach((card) => {
      let value;
      if (dataIndex === 'bank') {
        value = card.bank?.name;
      } else if (dataIndex === 'cardType') {
        value = card.cardType?.name;
      } else if (dataIndex === 'status') {
        value = card.status;
      } else if (dataIndex === 'isActive') {
        value = card.isActive ? 'Hoạt động' : 'Không hoạt động';
      }

      if (value) {
        uniqueValues.add(value);
      }
    });

    return Array.from(uniqueValues).map((value) => ({
      text: value,
      value: value,
    }));
  };

  const handleSoftDelete = async (id: number) => {
    try {
      await CreditCardService.softDeleteCreditCard(id);
      toast.success('Vô hiệu hóa thẻ thành công!');
      onRefresh(); // Refresh the list after soft deletion
    } catch (error) {
      console.error('Error soft deleting credit card:', error);
      toast.error('Có lỗi khi vô hiệu hóa thẻ. Vui lòng thử lại.');
    }
  };

  const handleHardDelete = async (id: number) => {
    try {
      await CreditCardService.hardDeleteCreditCard(id);
      toast.success('Xóa thẻ vĩnh viễn thành công!');
      onRefresh(); // Refresh the list after hard deletion
    } catch (error: any) {
      console.error('Error hard deleting credit card:', error);
      toast.error('Có lỗi khi xóa thẻ vĩnh viễn. Vui lòng thử lại.');
    }
  };

  const showSoftDeleteConfirm = (id: number) => {
    confirm({
      title: 'Xác nhận vô hiệu hóa thẻ',
      icon: <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />,
      content: (
        <div>
          <p>
            Bạn có chắc chắn muốn <strong>vô hiệu hóa</strong> thẻ này?
          </p>
          <p className="text-orange-600 text-sm">
            ⚠️ Thẻ sẽ được chuyển sang trạng thái "Không làm" và ngừng hoạt
            động.
          </p>
          <p className="text-gray-600 text-sm">
            Dữ liệu sẽ được giữ lại và có thể khôi phục sau này.
          </p>
        </div>
      ),
      okText: 'Vô hiệu hóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk() {
        handleSoftDelete(id);
      },
    });
  };

  const showHardDeleteConfirm = (id: number) => {
    confirm({
      title: 'Xác nhận xóa vĩnh viễn',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p>
            Bạn có chắc chắn muốn <strong>xóa vĩnh viễn</strong> thẻ này?
          </p>
          <p className="text-red-600 font-medium text-sm">
            ⚠️ CẢNH BÁO: Hành động này sẽ xóa thẻ và tất cả dữ liệu liên quan:
          </p>
          <ul className="text-red-600 text-sm ml-4 list-disc">
            <li>Tất cả lịch đáo hạn</li>
            <li>Tất cả giao dịch</li>
            <li>Tất cả khoản thanh toán</li>
          </ul>
          <p className="text-red-600 font-medium text-sm">
            ❌ Hành động này KHÔNG THỂ HOÀN TÁC!
          </p>
        </div>
      ),
      okText: 'XÓA VĨNH VIỄN',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk() {
        handleHardDelete(id);
      },
    });
  };

  const columns: ColumnsType<CreditCardResponse2> = [
    {
      title: 'Số thẻ',
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      width: 150,
      ellipsis: true,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số thẻ"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.cardNumber
          ?.toLowerCase()
          .includes((value as string).toLowerCase()) || false,
    },
    {
      title: 'Tên chủ thẻ',
      dataIndex: 'cardHolderName',
      key: 'cardHolderName',
      width: 150,
      ellipsis: true,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm tên chủ thẻ"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 90 }}
            >
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.cardHolderName
          ?.toLowerCase()
          .includes((value as string).toLowerCase()) || false,
    },
    {
      title: 'Ngân hàng',
      dataIndex: ['bank', 'name'],
      key: 'bank',
      width: 120,
      render: (_text: any, record: CreditCardResponse2) =>
        record.bank?.name || 'Unknown Bank',
      responsive: ['sm' as Breakpoint],
      filters: getUniqueValues('bank'),
      onFilter: (value: any, record: CreditCardResponse2) =>
        record.bank?.name === value,
    },
    {
      title: 'Loại thẻ',
      dataIndex: ['cardType', 'name'],
      key: 'cardType',
      width: 120,
      render: (_text: any, record: CreditCardResponse2) =>
        record.cardType?.name || 'Unknown Card Type',
      responsive: ['sm' as Breakpoint],
      filters: getUniqueValues('cardType'),
      onFilter: (value: any, record: CreditCardResponse2) =>
        record.cardType?.name === value,
    },
    {
      title: 'Ngày hết hạn',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      responsive: ['md' as Breakpoint],
      render: (text: string) => {
        if (!text) return '-';

        const expiryDate = parseISO(text); // Chuyển đổi sang kiểu Date
        const formattedDate = format(expiryDate, 'dd-MM-yyyy');

        let color = 'green'; // Mặc định là xanh lá (còn hạn)
        if (isToday(expiryDate)) {
          color = 'gold'; // Vàng nếu hết hạn hôm nay
        } else if (isPast(expiryDate)) {
          color = 'red'; // Đỏ nếu đã hết hạn
        }

        return <Tag color={color}>{formattedDate}</Tag>;
      },
    },

    {
      title: 'Hạn mức tín dụng',
      dataIndex: 'creditLimit',
      key: 'creditLimit',
      width: 140,
      responsive: ['lg' as Breakpoint],
      render: (text: number) =>
        new Intl.NumberFormat('vi-VN', {
          style: 'currency',
          currency: 'VND',
        }).format(text || 0),
    },
    {
      title: 'Ngày đến hạn hàng tháng',
      dataIndex: 'cyclePaymentDate',
      key: 'cyclePaymentDate',
      width: 180,
      responsive: ['xl' as Breakpoint],
      render: (text: number) => text || '-',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8, maxHeight: 300, overflowY: 'auto' }}>
          <div style={{ marginBottom: 8 }}>
            <Typography.Text strong>Chọn ngày:</Typography.Text>
            {cyclePaymentDateFilter.length > 0 && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                Đã chọn: {cyclePaymentDateFilter.length} ngày
              </div>
            )}
          </div>
          <div style={{ marginBottom: 8 }}>
            <Button
              size="small"
              onClick={() => {
                const allDays = Array.from({ length: 31 }, (_, i) => i + 1);
                setCyclePaymentDateFilter(allDays);
                setSelectedKeys(allDays);
              }}
              style={{ marginRight: 8 }}
            >
              Chọn tất cả
            </Button>
            <Button
              size="small"
              onClick={() => {
                setCyclePaymentDateFilter([]);
                setSelectedKeys([]);
              }}
            >
              Bỏ chọn tất cả
            </Button>
          </div>
          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
            <div key={day} style={{ marginBottom: 4 }}>
              <input
                type="checkbox"
                checked={cyclePaymentDateFilter.includes(day)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    const newFilter = [...cyclePaymentDateFilter, day];
                    setCyclePaymentDateFilter(newFilter);
                    setSelectedKeys(newFilter);
                  } else {
                    const newFilter = cyclePaymentDateFilter.filter(
                      (d) => d !== day
                    );
                    setCyclePaymentDateFilter(newFilter);
                    setSelectedKeys(newFilter);
                  }
                }}
                style={{ marginRight: 8 }}
              />
              <span>Ngày {day}</span>
            </div>
          ))}
          <div
            style={{
              marginTop: 8,
              borderTop: '1px solid #d9d9d9',
              paddingTop: 8,
            }}
          >
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90 }}
              >
                Áp dụng
              </Button>
              <Button
                onClick={() => {
                  clearFilters?.();
                  setCyclePaymentDateFilter([]);
                }}
                size="small"
                style={{ width: 90 }}
              >
                Xóa
              </Button>
            </Space>
          </div>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: CreditCardResponse2) => {
        if (cyclePaymentDateFilter.length === 0) return true;
        return cyclePaymentDateFilter.includes(record.cyclePaymentDate || 0);
      },
      filtered: cyclePaymentDateFilter.length > 0,
    },
    {
      title: 'Phí mặc định (%)',
      dataIndex: 'defaultFeeRate',
      key: 'defaultFeeRate',
      width: 180,
      responsive: ['xl' as Breakpoint],
      render: (text: number) => text || '-',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      responsive: ['md' as Breakpoint],
      filters: [
        { text: 'Lưu thẻ', value: 'LUU_THE' },
        { text: 'Trả thẻ', value: 'TRA_THE' },
      ],
      onFilter: (value: any, record: CreditCardResponse2) =>
        record.status === value,
      render: (status: string) =>
        status ? (
          <Tag
            color={
              status === 'LUU_THE'
                ? 'green'
                : status === 'TRA_THE'
                  ? 'blue'
                  : 'default'
            }
          >
            {status === 'LUU_THE'
              ? 'Lưu thẻ'
              : status === 'TRA_THE'
                ? 'Trả thẻ'
                : status}
          </Tag>
        ) : (
          '-'
        ),
    },
    {
      title: 'Khả dụng',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      responsive: ['md' as Breakpoint],
      filters: [
        { text: 'Hoạt động', value: true },
        { text: 'Không hoạt động', value: false },
      ],
      onFilter: (value: any, record: CreditCardResponse2) =>
        record.isActive === value,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      ),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: 120,
      responsive: ['lg' as Breakpoint],
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: 'Hành động',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size={0} className="flex flex-wrap justify-center">
          <Button
            type="link"
            size="large"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedCard(record); // Đặt thẻ vào context
              navigate('/card-detail'); // Chuyển trang
            }}
            title="Xem chi tiết thẻ"
          />

          <EditCreditCardModal
            creditCard={record}
            customerId={customerId}
            onSuccess={onRefresh}
          />

          <Dropdown
            menu={{
              items: [
                {
                  key: 'soft-delete',
                  label: 'Vô hiệu hóa',
                  icon: <DeleteOutlined style={{ color: '#fa8c16' }} />,
                  onClick: () => showSoftDeleteConfirm(record.id),
                  danger: true,
                },
                {
                  key: 'hard-delete',
                  label: (
                    <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
                      Xóa vĩnh viễn
                    </span>
                  ),
                  icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
                  onClick: () => showHardDeleteConfirm(record.id),
                  danger: true,
                },
              ],
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Tooltip title="Tùy chọn xóa">
              <Button
                type="link"
                size="large"
                icon={<MoreOutlined />}
                className="text-gray-600 hover:text-gray-800"
              />
            </Tooltip>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {creditCards.length > 0 ? (
        <>
          {cyclePaymentDateFilter.length > 0 && (
            <div
              style={{
                marginBottom: 8,
                padding: '8px 12px',
                backgroundColor: '#f0f8ff',
                borderRadius: 4,
              }}
            >
              <Typography.Text type="secondary">
                Hiển thị {filteredCreditCards.length} thẻ (lọc theo ngày:{' '}
                {cyclePaymentDateFilter.sort((a, b) => a - b).join(', ')})
              </Typography.Text>
            </div>
          )}
          <Table
            columns={columns}
            dataSource={filteredCreditCards}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filteredCreditCards.length,
              onChange: setCurrentPage,
              showSizeChanger: false,
              position: ['bottomRight'],
              size: 'small',
            }}
            scroll={{ x: 'max-content' }}
            size="middle"
            bordered
          />
        </>
      ) : (
        <Typography.Text>Khách hàng chưa có thẻ nào.</Typography.Text>
      )}
    </div>
  );
};

export default CreditCardList;
